import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../services/apiService';

const DataroomTab = () => {
  const { id } = useParams();
  const [round1Items, setRound1Items] = useState([]);
  const [round2Items, setRound2Items] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAccordion, setOpenAccordion] = useState(null);

  // Add state for rejection modal
  const [rejectionModal, setRejectionModal] = useState({
    isOpen: false,
    item: null,
    reason: ''
  });

  // Add state for document description modal
  const [documentModal, setDocumentModal] = useState({
    isOpen: false,
    item: null,
    title: '',
    description: ''
  });

  // Add state for information query modal
  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    item: null,
    title: '',
    body: ''
  });

  // Add state for zip list
  const [zipList, setZipList] = useState([]);
  const [zipListLoading, setZipListLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchDataRoomItems();
      fetchZipList();
    }
  }, [id]);

  const fetchDataRoomItems = async () => {
    try {
      setLoading(true);
      
      // Fetch both rounds in parallel
      const [round1Data, round2Data] = await Promise.all([
        apiService.get(`/property-data-room/admin?propertyId=${id}&round=1`),
        apiService.get(`/property-data-room/admin?propertyId=${id}&round=2`)
      ]);
      
      setRound1Items(round1Data);
      setRound2Items(round2Data);
      setError(null);
    } catch (err) {
      console.error('Error fetching data room items:', err);
      setError('Failed to load property data room items');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccordion = (accordion) => {
    setOpenAccordion(openAccordion === accordion ? null : accordion);
  };


  const handleAddLevel = (parentItem) => {
    // TODO: Implement add level functionality
    console.log('Adding new level under:', parentItem.displayName);
    alert(`Add new level functionality under "${parentItem.displayName}" to be implemented.`);
  };

  const handleHideLevel = async (item) => {
    try {
      // If currently visible (true), send false to hide it
      // If currently hidden (false), send true to show it
      const newVisibility = !item.isVisible;
      
      const requestBody = {
        isVisible: newVisibility
      };

      console.log('Toggling visibility for:', item.displayName, 'from', item.isVisible, 'to:', newVisibility);

      await apiService.put(`/property-data-room/${item.id}/visibility`, requestBody);
      
      alert(`Level ${newVisibility ? 'shown' : 'hidden'} successfully!`);
      
      // Refresh the data room items to reflect the change
      fetchDataRoomItems();
      
    } catch (err) {
      console.error('Error toggling visibility:', err);
      alert('Failed to toggle visibility. Please try again.');
    }
  };

  const handlePublishToggle = (item, isPublished) => {
    // TODO: Implement publish/unpublish functionality
    console.log('Toggling publish for:', item.displayName, 'to:', isPublished);
    alert(`Publish toggle for "${item.displayName}" to ${isPublished ? 'published' : 'unpublished'} to be implemented.`);
  };

  // Add function to handle accept/reject files
  const handleFileApproveReject = async (item, status) => {
    try {
      const requestBody = {
        adminUserId: 4,
        publishYN: status === 'Accepted',
        rejectedReason: status === 'Rejected' ? 'Rejected by admin' : null
      };

      console.log('Submitting file approval/rejection:', requestBody);

      await apiService.put(`/property-data-room/${item.fileId}/decision`, requestBody);
      
      alert(`File ${status} successfully!`);
      
      // Refresh the data room items
      fetchDataRoomItems();
      
    } catch (err) {
      console.error('Error submitting file approval/rejection:', err);
      alert(`Failed to ${status.toLowerCase()} file. Please try again.`);
    }
  };

  // Add function to handle reject with reason
  const handleRejectWithReason = async () => {
    if (!rejectionModal.item || rejectionModal.reason.trim().length < 10) {
      alert('Please provide a rejection reason with at least 10 characters.');
      return;
    }

    try {
      const requestBody = {
        adminUserId: 4,
        publishYN: false,
        rejectedReason: rejectionModal.reason.trim()
      };

      console.log('Submitting file rejection with reason:', requestBody);

      await apiService.put(`/property-data-room/${rejectionModal.item.fileId}/decision`, requestBody);
      
      alert('File rejected successfully!');
      
      // Close modal and refresh data
      setRejectionModal({ isOpen: false, item: null, reason: '' });
      fetchDataRoomItems();
      
    } catch (err) {
      console.error('Error submitting file rejection:', err);
      alert('Failed to reject file. Please try again.');
    }
  };

  // Add function to handle accept files
  const handleAcceptFile = async (item) => {
    try {
      const requestBody = {
        adminUserId: 4,
        publishYN: true,
        rejectedReason: null
      };

      console.log('Submitting file acceptance:', requestBody);

      await apiService.put(`/property-data-room/${item.fileId}/decision`, requestBody);
      
      alert('File accepted successfully!');
      
      // Refresh the data room items
      fetchDataRoomItems();
      
    } catch (err) {
      console.error('Error submitting file acceptance:', err);
      alert('Failed to accept file. Please try again.');
    }
  };

  const handleExtDDDocument = (item) => {
    setDocumentModal({
      isOpen: true,
      item: item,
      title: '',
      description: ''
    });
  };

  const handleSubmitDocumentRequest = async () => {
    if (!documentModal.item || documentModal.title.trim().length === 0) {
      alert('Please provide a document title.');
      return;
    }

    if (documentModal.description.trim().length === 0) {
      alert('Please provide a document description.');
      return;
    }

    try {
      const requestBody = {
        documenttitle: documentModal.title.trim(),
        documentdescription: documentModal.description.trim(),
        propertyDataRoomId: documentModal.item.id,
        requesttype: "file"
      };

      console.log('Submitting document request:', requestBody);

      await apiService.post('/property-data-room/addextDDrequest', requestBody);
      
      alert('Document request submitted successfully!');
      
      // Close modal and reset
      setDocumentModal({ isOpen: false, item: null, title: '', description: '' });
      
    } catch (err) {
      console.error('Error submitting document request:', err);
      alert('Failed to submit document request. Please try again.');
    }
  };

  const handleExtDDInformation = (item) => {
    setInformationModal({
      isOpen: true,
      item: item,
      title: '',
      body: ''
    });
  };

  const handleSubmitInformationRequest = async () => {
    if (!informationModal.item || informationModal.title.trim().length === 0) {
      alert('Please provide a query title.');
      return;
    }

    if (informationModal.body.trim().length === 0) {
      alert('Please provide a query description.');
      return;
    }

    try {
      const requestBody = {
        queryTitle: informationModal.title.trim(),
        queryBody: informationModal.body.trim(),
        propertyDataRoomId: informationModal.item.id,
        requesttype: "text"
      };

      console.log('Submitting information request:', requestBody);

      await apiService.post('/property-data-room/addextDDrequest', requestBody);
      
      alert('Information request submitted successfully!');
      
      // Close modal and reset
      setInformationModal({ isOpen: false, item: null, title: '', body: '' });
      
    } catch (err) {
      console.error('Error submitting information request:', err);
      alert('Failed to submit information request. Please try again.');
    }
  };

  // Add function to fetch zip list
  const fetchZipList = async () => {
    try {
      setZipListLoading(true);
      const data = await apiService.get(`/property-data-room/zip-list/${id}`);
      setZipList(data || []);
    } catch (err) {
      console.error('Error fetching zip list:', err);
      setZipList([]);
    } finally {
      setZipListLoading(false);
    }
  };

  // Add function to handle create zip for round 1
  const handleCreateZipRound1 = async () => {
    try {
      const requestBody = {
        propertyId: parseInt(id),
        round: 1
      };

      console.log('Creating zip for round 1:', requestBody);

      const response = await apiService.post('/property-data-room/create-zip', requestBody);
      
      alert(response.message || 'Zip file for round 1 created successfully!');
      
      // Refresh the zip list after creation
      await fetchZipList();
      
    } catch (err) {
      console.error('Error creating zip for round 1:', err);
      alert('Failed to create zip for round 1. Please try again.');
    }
  };

  // Add function to handle create zip for round 2
  const handleCreateZipRound2 = async () => {
    try {
      const requestBody = {
        propertyId: parseInt(id),
        round: 2
      };

      console.log('Creating zip for round 2:', requestBody);

      const response = await apiService.post('/property-data-room/create-zip', requestBody);
      
      alert(response.message || 'Zip file for round 2 created successfully!');
      
      // Refresh the zip list after creation
      await fetchZipList();
      
    } catch (err) {
      console.error('Error creating zip for round 2:', err);
      alert('Failed to create zip for round 2. Please try again.');
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderItem = (item, index, itemsList) => {
    const isDataRoom = item.itemType === 'DATA_ROOM';
    let indentLevel;
    
    if (isDataRoom) {
      // Level headings use their level for indentation
      indentLevel = item.level - 1;
    } else {
      // For documents, find their parent using checkListId instead of id
      const parentItem = itemsList.find(parent => 
        parent.checkListId === item.parentId && parent.itemType === 'DATA_ROOM'
      );
      
      if (parentItem) {
        // Calculate parent's indentation position
        const parentIndentLevel = parentItem.level - 1;
        // Document is indented TWO levels more than its parent's position
        indentLevel = parentIndentLevel + 2;
      } else {
        // Fallback: If parent not found, use a reasonable default indentation
        indentLevel = Math.max(2, (item.level || 1) + 1);
      }
    }
    
    if (isDataRoom) {
      // Render as a level heading with add-level icon (only if item is visible)
      return (
        <div 
          key={`${item.id}-${index}`}
          className="data-room-item heading"
          style={{ 
            marginLeft: `${indentLevel * 20}px`,
            textDecoration: item.isVisible === false ? 'line-through' : 'none',
            opacity: item.isVisible === false ? 0.6 : 1,
            borderLeft: item.isVisible === false ? '4px solid red' : '2px solid green',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div className="item-header" style={{ flex: 1 }}>
            <span className="item-name">{item.displayName}</span>
            <div className="item-actions">
              <button 
                className="action-icon visibility-icon"
                onClick={() => handleHideLevel(item)}
                title={item.isVisible === false ? "Make this level visible" : "Hide this level"}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: item.isVisible === false ? 'green' : 'red'
                }}
              >
                {item.isVisible === false ? '👁️' : '👁️‍🗨️'}
              </button>
              {item.isVisible !== false && (
                <button 
                  className="action-icon add-level-icon"
                  onClick={() => handleAddLevel(item)}
                  title="Add sub-level"
                >
                  ➕
                </button>
              )}
            </div>
          </div>
          <div className="extdd-column" style={{ 
            width: '80px', 
            display: 'flex', 
            gap: '4px', 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <button 
              className="extdd-document-icon"
              onClick={() => handleExtDDDocument(item)}
              title="Describe document required"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#0066cc',
                padding: '4px'
              }}
            >
              📄
            </button>
            <button 
              className="extdd-information-icon"
              onClick={() => handleExtDDInformation(item)}
              title="Describe information required"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#0066cc',
                padding: '4px'
              }}
            >
              ℹ️
            </button>
          </div>
        </div>
      );
    } else {
      // Render as a downloadable file with approve/reject icons
      return (
        <div 
          key={`${item.id}-${index}`}
          className="data-room-item file"
          style={{ 
            marginLeft: `${indentLevel * 20}px`,
            textDecoration: item.isVisible === false ? 'line-through' : 'none',
            opacity: item.isVisible === false ? 0.6 : 1,
            borderLeft: item.isVisible === false ? '4px solid red' : '2px solid green',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div className="item-header" style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
              {item.isPublished && (
                <span style={{ color: 'green', fontSize: '16px' }}>▶</span>
              )}
              {item.isRejected && (
                <>
                  <span style={{ color: 'red', fontSize: '16px' }}>⚠️</span>
                  <span
                    style={{ 
                      color: 'red', 
                      fontSize: '14px',
                      cursor: 'help'
                    }}
                    title={item.rejectedReason || 'No reason provided'}
                  >
                    ℹ️
                  </span>
                </>
              )}
              <span className="item-name">{item.displayName}</span>
            </div>
            <div className="item-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button 
                className="action-icon visibility-icon"
                onClick={() => handleHideLevel(item)}
                title={item.isVisible === false ? "Make this file visible" : "Hide this file"}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: item.isVisible === false ? 'green' : 'red'
                }}
              >
                {item.isVisible === false ? '👁️' : '👁️‍🗨️'}
              </button>
              <button 
                className="accept-btn"
                onClick={() => handleFileApproveReject(item, 'Accepted')}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: 'green'
                }}
                title="Accept file"
              >
                ✓
              </button>
              <button 
                className="reject-btn"
                onClick={() => setRejectionModal({ isOpen: true, item: item, reason: '' })}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: 'red'
                }}
                title="Reject file"
              >
                ✗
              </button>
            </div>
          </div>
          <div className="extdd-column" style={{ 
            width: '80px', 
            display: 'flex', 
            gap: '4px', 
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <button 
              className="extdd-document-icon"
              onClick={() => handleExtDDDocument(item)}
              title="Describe document required"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#0066cc',
                padding: '4px'
              }}
            >
              📄
            </button>
            <button 
              className="extdd-information-icon"
              onClick={() => handleExtDDInformation(item)}
              title="Describe information required"
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                fontSize: '16px',
                cursor: 'pointer',
                color: '#0066cc',
                padding: '4px'
              }}
            >
              ℹ️
            </button>
          </div>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="tab-content">
        <div className="loading-state">
          <p>Loading property data room items...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-content">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchDataRoomItems} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (round1Items.length === 0 && round2Items.length === 0) {
    return (
      <div className="tab-content">
        <h3>Dataroom</h3>
        <p>No data room items found for this property.</p>
      </div>
    );
  }

  return (
    <div className="tab-content">
      <h3>Dataroom</h3>
      
      {/* Round 1 Accordion */}
      <div className="accordion-container">
        <div 
          className="accordion-header"
          onClick={() => toggleAccordion('round1')}
          style={{
            backgroundColor: '#f5f5f5',
            padding: '12px 16px',
            border: '1px solid #ddd',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h4 style={{ margin: 0, color: '#333' }}>Round 1 - Virtual Data Room</h4>
          <span style={{ fontSize: '18px' }}>
            {openAccordion === 'round1' ? '▼' : '▶'}
          </span>
        </div>
        {openAccordion === 'round1' && (
          <div className="accordion-content" style={{ border: '1px solid #ddd', borderTop: 'none' }}>
            <div className="data-room-container" style={{ padding: '16px' }}>
              {round1Items.map((item, index) => renderItem(item, index, round1Items))}
            </div>
          </div>
        )}
      </div>

      {/* Round 2 Accordion */}
      <div className="accordion-container" style={{ marginTop: '16px' }}>
        <div 
          className="accordion-header"
          onClick={() => toggleAccordion('round2')}
          style={{
            backgroundColor: '#f5f5f5',
            padding: '12px 16px',
            border: '1px solid #ddd',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <h4 style={{ margin: 0, color: '#333' }}>Round 2 - Due Diligence</h4>
          <span style={{ fontSize: '18px' }}>
            {openAccordion === 'round2' ? '▼' : '▶'}
          </span>
        </div>
        {openAccordion === 'round2' && (
          <div className="accordion-content" style={{ border: '1px solid #ddd', borderTop: 'none' }}>
            <div className="data-room-container" style={{ padding: '16px' }}>
              {round2Items.map((item, index) => renderItem(item, index, round2Items))}
            </div>
          </div>
        )}
      </div>

      {/* Zip Files Section */}
      <div style={{ marginTop: '24px' }}>
        <h4 style={{ margin: '0 0 16px 0', color: 'var(--cbre-text)' }}>Zip Files</h4>
        
        {/* Trigger Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={handleCreateZipRound1}
            style={{
              padding: '10px 20px',
              backgroundColor: '#0066cc',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Create zip for round 1
          </button>
          <button
            onClick={handleCreateZipRound2}
            style={{
              padding: '10px 20px',
              backgroundColor: '#0066cc',
              border: 'none',
              borderRadius: '4px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Create zip for round 2
          </button>
        </div>

        {/* Zip List */}
        {zipListLoading ? (
          <p style={{ color: 'var(--cbre-text-secondary)', margin: '16px 0' }}>Loading zip files...</p>
        ) : zipList.length === 0 ? (
          <p style={{ color: 'var(--cbre-text-secondary)', margin: '16px 0' }}>No zip files found.</p>
        ) : (
          <div className="media-table-container">
            <table className="media-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Round</th>
                  <th>Generator</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>File Size</th>
                </tr>
              </thead>
              <tbody>
                {zipList.map((zipItem, index) => (
                  <tr key={index}>
                    <td>{formatDate(zipItem.dateOn)}</td>
                    <td>{zipItem.round}</td>
                    <td>{zipItem.generator}</td>
                    <td>{zipItem.fileDescription}</td>
                    <td style={{ textAlign: 'right' }}>{formatFileSize(zipItem.fileSize)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rejection Modal */}
      {rejectionModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--cbre-bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid var(--cbre-border)',
            minWidth: '400px',
            maxWidth: '600px'
          }}>
            <h4 style={{ 
              margin: '0 0 16px 0',
              color: 'var(--cbre-text)'
            }}>
              Reject File: {rejectionModal.item?.displayName}
            </h4>
            
            <div>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                color: 'var(--cbre-text)',
                fontWeight: 'bold'
              }}>
                Reason for rejection:
              </label>
              <textarea
                value={rejectionModal.reason}
                onChange={(e) => setRejectionModal({...rejectionModal, reason: e.target.value})}
                placeholder="Please provide a detailed reason for rejecting this file..."
                style={{
                  width: '100%',
                  height: '120px',
                  padding: '12px',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  color: 'var(--cbre-text)',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ 
                fontSize: '12px', 
                color: 'var(--cbre-text-secondary)',
                marginTop: '5px'
              }}>
                {rejectionModal.reason.length}/10 characters minimum
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setRejectionModal({ isOpen: false, item: null, reason: '' })}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: 'var(--cbre-text)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleRejectWithReason}
                disabled={rejectionModal.reason.trim().length < 10}
                style={{
                  padding: '8px 16px',
                  backgroundColor: rejectionModal.reason.trim().length < 10 ? 'var(--cbre-bg-secondary)' : '#dc3545',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: rejectionModal.reason.trim().length < 10 ? 'var(--cbre-text-secondary)' : 'white',
                  cursor: rejectionModal.reason.trim().length < 10 ? 'not-allowed' : 'pointer'
                }}
              >
                Submit Rejection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Request Modal */}
      {documentModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--cbre-bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid var(--cbre-border)',
            minWidth: '400px',
            maxWidth: '600px'
          }}>
            <h4 style={{ 
              margin: '0 0 16px 0',
              color: 'var(--cbre-text)'
            }}>
              Describe Document Required: {documentModal.item?.displayName}
            </h4>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                color: 'var(--cbre-text)',
                fontWeight: 'bold'
              }}>
                Document Title:
              </label>
              <input
                type="text"
                value={documentModal.title}
                onChange={(e) => setDocumentModal({...documentModal, title: e.target.value})}
                placeholder="Enter document title..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  color: 'var(--cbre-text)',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                color: 'var(--cbre-text)',
                fontWeight: 'bold'
              }}>
                Document Description:
              </label>
              <textarea
                value={documentModal.description}
                onChange={(e) => setDocumentModal({...documentModal, description: e.target.value})}
                placeholder="Describe the document required..."
                style={{
                  width: '100%',
                  height: '120px',
                  padding: '12px',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  color: 'var(--cbre-text)',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setDocumentModal({ isOpen: false, item: null, title: '', description: '' })}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: 'var(--cbre-text)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDocumentRequest}
                disabled={documentModal.title.trim().length === 0 || documentModal.description.trim().length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (documentModal.title.trim().length === 0 || documentModal.description.trim().length === 0) ? 'var(--cbre-bg-secondary)' : '#0066cc',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: (documentModal.title.trim().length === 0 || documentModal.description.trim().length === 0) ? 'var(--cbre-text-secondary)' : 'white',
                  cursor: (documentModal.title.trim().length === 0 || documentModal.description.trim().length === 0) ? 'not-allowed' : 'pointer'
                }}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information Request Modal */}
      {informationModal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'var(--cbre-bg-primary)',
            padding: '24px',
            borderRadius: '8px',
            border: '1px solid var(--cbre-border)',
            minWidth: '400px',
            maxWidth: '600px'
          }}>
            <h4 style={{ 
              margin: '0 0 16px 0',
              color: 'var(--cbre-text)'
            }}>
              Describe Information Required: {informationModal.item?.displayName}
            </h4>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                color: 'var(--cbre-text)',
                fontWeight: 'bold'
              }}>
                Query Title:
              </label>
              <input
                type="text"
                value={informationModal.title}
                onChange={(e) => setInformationModal({...informationModal, title: e.target.value})}
                placeholder="Enter query title..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  color: 'var(--cbre-text)',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block',
                marginBottom: '8px',
                color: 'var(--cbre-text)',
                fontWeight: 'bold'
              }}>
                Query Description:
              </label>
              <textarea
                value={informationModal.body}
                onChange={(e) => setInformationModal({...informationModal, body: e.target.value})}
                placeholder="Describe the information required..."
                style={{
                  width: '100%',
                  height: '120px',
                  padding: '12px',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  color: 'var(--cbre-text)',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setInformationModal({ isOpen: false, item: null, title: '', body: '' })}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'var(--cbre-bg-secondary)',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: 'var(--cbre-text)',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitInformationRequest}
                disabled={informationModal.title.trim().length === 0 || informationModal.body.trim().length === 0}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (informationModal.title.trim().length === 0 || informationModal.body.trim().length === 0) ? 'var(--cbre-bg-secondary)' : '#0066cc',
                  border: '1px solid var(--cbre-border)',
                  borderRadius: '4px',
                  color: (informationModal.title.trim().length === 0 || informationModal.body.trim().length === 0) ? 'var(--cbre-text-secondary)' : 'white',
                  cursor: (informationModal.title.trim().length === 0 || informationModal.body.trim().length === 0) ? 'not-allowed' : 'pointer'
                }}
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataroomTab;
